//#region Imports
import { AuthenticationService } from '@app/member-response-portal/service/authentication.service';
import { Component, OnInit } from '@angular/core';
import { CONTACT_INFORMATION_VALIDATIONS, NO_SYMBOLS_OR_SPACES_PATTERN, PAGES_ROUTES, PHONE_FORMAT } from '@app/member-response-portal/model/constants';
import { ContactInformationDto } from '@app/member-response-portal/model/contactInformationDto';
import { ContactInformationService } from '@app/member-response-portal/service/contact-information.service';
import { FormBuilder, FormControl, FormGroup, Validators, } from '@angular/forms';
import { GenericMemberResponsePortalResponseDto } from '@app/core/models/genericMembeResponsePortalResponseDto';
import { Helper } from '@rawlings/core';
import { MemberResponseHelper } from '@app/member-response-portal/shared/member-response-helper';
import { NavigationService } from '@app/member-response-portal/service/navigation.service';
import { NotificationRequestService } from '@app/member-response-portal/service/notification-request.service';
import { NotifierService } from '@app/core/services/notifier-service';
import { SharedMemberResponseService } from '@app/member-response-portal/service/shared-member-response.service';
import { SubmitContactInformationResultDto } from '@app/member-response-portal/model/submitContactInformationResultDto';
import { SurveyResponseDto } from '@app/member-response-portal/model/surveyResponseDto';

//#endregion Imports
@Component({
    selector: 'app-contact-information',
    templateUrl: './contact-information.component.html',
    styleUrls: ['./contact-information.component.scss']
})
export class ContactInformationComponent implements OnInit {
    //#region Public Variables
    public EmailMaxLength: number = CONTACT_INFORMATION_VALIDATIONS.EMAIL_MAX_LENGTH;
    public Form: FormGroup = new FormGroup({});
    public PhoneFormat: string = PHONE_FORMAT;
    public UserNameMaxLength: number = CONTACT_INFORMATION_VALIDATIONS.USERNAME_MAX_LENGTH;
    //#endregion Public Variables

    //#region Protected Variables
    //#endregion Protected Variables

    //#region Private Variables
    private initialFormValue: any = null;
    private queueLetterId: number = null;
    private surveyResponseId: number = null;
    //#endregion Private Variables

    //#region Properties
    public get SurveyResponseId(): FormControl{
        return this.Form.get('surveyResponseId') as FormControl;
    }

    public get QueueLetterId(): FormControl{
        return this.Form.get('queueLetterId') as FormControl;
    }

    public get DayTimePhone(): FormControl{
        return this.Form.get('dayTimePhone') as FormControl;
    }

    public get Email(): FormControl{
        return this.Form.get('email') as FormControl;
    }

    public get UserName(): FormControl {
        return this.Form.get('userName') as FormControl;
    }

    private get contactInformationDto(): ContactInformationDto {
        const contactInfoDto: ContactInformationDto = {
                SurveyResponseId: this.SurveyResponseId.value,
                QueueLetterId: this.QueueLetterId.value,
                Username: this.UserName.value,
                DayTimePhone: this.DayTimePhone.value,
                Email: this.Email.value,
        };
        return contactInfoDto;
    }
    //#endregion Properties

    //#region Constructor and Angular Life Cycle
    constructor(
        private authenticationService: AuthenticationService,
        private contactInformationService: ContactInformationService,
        private fb: FormBuilder,
        private navigationService: NavigationService,
        private notificationRequestService: NotificationRequestService,
        private notifierService: NotifierService,
        private sharedMemberResponseService: SharedMemberResponseService
    ) {}

    public ngOnInit(): void {
        this.queueLetterId = this.sharedMemberResponseService.GetQueueLetterId();
        this.surveyResponseId = this.sharedMemberResponseService.GetSurveyResponseId();

        if (Helper.IsEmpty(this.queueLetterId)) {
            this.authenticationService.LogoutAndRedirectToLogin();
        }

        this.Form = this.fb.group({
            surveyResponseId: [this.surveyResponseId],
            queueLetterId: [this.queueLetterId, Validators.required],
            userName: ['', [Validators.required, Validators.pattern(NO_SYMBOLS_OR_SPACES_PATTERN)]],
            dayTimePhone: ['', [Validators.required, Validators.pattern(Helper.RegExOptionalPrefix + Helper.RegExPhoneExpression)]],
            email: ['', [Validators.required, Validators.email, Validators.pattern(Helper.RegExEmailExpression)]],
        });

        this.loadData();
        this.initialFormValue = this.Form.value;
    }
    //#endregion Constructor and Angular Life Cycle

    //#region Public Methods

    public OnSubmit(): void {
        if(!this.Form.valid){
            this.notifierService.DisplayErrorMessage('Mandatory fields must be filled out.');
            return;
        }

        const hasFormChanged: boolean = MemberResponseHelper.HasFormChanged(this.Form, this.initialFormValue);
        if(!hasFormChanged){
            this.navigationService.NavigateTo(PAGES_ROUTES.CONFIRM_HEALTH_INSURANCE);
            return;
        }

        this.contactInformationService
        .Save(this.contactInformationDto)
        .subscribe((result: GenericMemberResponsePortalResponseDto<SubmitContactInformationResultDto>) => {
            if (result.Success) {

                this.sharedMemberResponseService.SetSurveyResponse(this.buildSurveyResponse(result.Data?.SurveyResponseId));
                this.navigationService.NavigateTo(PAGES_ROUTES.CONFIRM_HEALTH_INSURANCE);
            } else {
                this.notificationRequestService.ShowFailedRequestAndLogout(null);
            }
        });

    }

    public SetErrorTooltipMessage(value: string, message: string) : string {
        if (this.Form.get(value).hasError('pattern')) {
            return `Invalid ${message}`;
        }

        return null;
    }
    //#endregion Public Methods

    //#region Private Methods
    private buildSurveyResponse(surveyResponseId: number): SurveyResponseDto{
        const surveyResponseDto: SurveyResponseDto = this.sharedMemberResponseService.GetSurveyResponse() || new SurveyResponseDto();

        surveyResponseDto.SurveyResponseId = surveyResponseId;
        surveyResponseDto.Date = new Date();
        surveyResponseDto.DayTimePhone = this.DayTimePhone.value;
        surveyResponseDto.Email = this.Email.value;
        surveyResponseDto.Username = this.UserName.value;

        return surveyResponseDto;
    }

    private loadData() : void {
        const surveyResponseData: SurveyResponseDto = this.sharedMemberResponseService.GetSurveyResponse();

        if(surveyResponseData){
          this.Form.patchValue({
            dayTimePhone: surveyResponseData.DayTimePhone,
            email: surveyResponseData.Email,
            userName: surveyResponseData.Username,
          });
        }
    }
    //#region Private Methods
}
